import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faTimes,
  faDownload,
  faMinus,
  faPlus,
} from '@fortawesome/free-solid-svg-icons'
import { useDocumentViewer } from '../contexts/DocumentViewerContext'

type ViewerKind = 'pdf' | 'image' | 'unknown'

function resolveViewerKind(type: string, url: string | null): ViewerKind {
  const t = String(type || '').toLowerCase()
  const href = String(url || '')
  if (
    t.includes('pdf') ||
    href.toLowerCase().includes('.pdf')
  ) {
    return 'pdf'
  }
  if (
    t.includes('image') ||
    t === 'png' ||
    t === 'jpg' ||
    t === 'jpeg' ||
    t === 'gif' ||
    t === 'webp' ||
    t === 'svg' ||
    t === 'bmp' ||
    /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(href)
  ) {
    return 'image'
  }
  return 'unknown'
}

const DocumentViewerModal: React.FC = () => {
  const {
    isOpen,
    documentTitle,
    documentType,
    documentUrl,
    documentError,
    closeDocument,
  } = useDocumentViewer()
  const [imageZoom, setImageZoom] = useState(1)

  const kind = useMemo(
    () => resolveViewerKind(documentType, documentUrl),
    [documentType, documentUrl],
  )

  useEffect(() => {
    setImageZoom(1)
  }, [documentUrl, isOpen])

  useEffect(() => {
    if (!isOpen) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        closeDocument()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [isOpen, closeDocument])

  if (!isOpen) return null

  const hasDocument = Boolean(documentUrl) && !documentError
  const typeLabel =
    kind === 'image' ? 'Image' : kind === 'pdf' ? 'PDF' : documentType || 'File'

  const overlay = (
    <div
      className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/50 p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={documentTitle}
      onClick={closeDocument}
    >
      <div
        className="flex h-full max-h-[min(56rem,calc(100dvh-1.5rem))] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-base-300 bg-base-100 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-base-300 px-4 py-3">
          <div className="min-w-0">
            <h3 className="truncate font-bold text-lg">{documentTitle}</h3>
            <p className="text-sm text-base-content/60">{typeLabel}</p>
          </div>
          <button
            type="button"
            onClick={closeDocument}
            className="btn btn-sm btn-circle btn-ghost"
            aria-label="Close viewer"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2 px-4 py-3">
          {hasDocument ? (
            <a href={documentUrl!} download className="btn btn-sm btn-outline">
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Download
            </a>
          ) : null}
          {hasDocument && kind === 'image' ? (
            <div className="join">
              <button
                type="button"
                className="btn btn-sm btn-outline join-item"
                aria-label="Zoom out"
                onClick={() => setImageZoom((z) => Math.max(0.25, Math.round((z - 0.25) * 100) / 100))}
              >
                <FontAwesomeIcon icon={faMinus} />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline join-item min-w-16"
                aria-label="Reset zoom"
                onClick={() => setImageZoom(1)}
              >
                {Math.round(imageZoom * 100)}%
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline join-item"
                aria-label="Zoom in"
                onClick={() => setImageZoom((z) => Math.min(4, Math.round((z + 0.25) * 100) / 100))}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 bg-base-200 p-3">
          {documentError ? (
            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-error">
              {documentError}
            </div>
          ) : !documentUrl ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 text-base-content/60">
              <div className="loading loading-spinner loading-lg" />
              <span>Loading…</span>
            </div>
          ) : kind === 'image' ? (
            <div className="flex h-full items-center justify-center overflow-auto">
              <img
                src={documentUrl}
                alt={documentTitle}
                className="max-h-none origin-center select-none"
                style={{ transform: `scale(${imageZoom})` }}
                draggable={false}
              />
            </div>
          ) : kind === 'pdf' ? (
            <iframe
              src={documentUrl}
              title={documentTitle}
              className="h-full w-full rounded border-0 bg-base-100"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-base-content/70">
              <p>Preview is not available for this file type.</p>
              <a href={documentUrl} download className="btn btn-sm btn-primary">
                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                Download file
              </a>
            </div>
          )}
        </div>

        <div className="flex shrink-0 justify-end border-t border-base-300 px-4 py-3">
          <button type="button" className="btn" onClick={closeDocument}>
            Close
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(overlay, document.body)
}

export default DocumentViewerModal
